import {sidebar} from "navigation";
import {useLayoutEffect} from "react";
import {toggleMenu} from "global/slices";
import {useTheme} from "styled-components";
import {useLocation} from "react-router-dom";
import {IconButton} from "components/@button";
import {Frame, Container, NavItem} from "./styled";
import {useAppDispatch, useAppSelector} from "hooks";
import {AnimatePresence, motion} from "framer-motion";
import {buildAvatarPlaceholder, storage} from "utils";
import {ChevronLeft, ChevronRight} from "react-feather";
import {
  Row,
  Line,
  Avatar,
  Column,
  Absolute,
  Typography,
} from "components/@system";

interface SidebarProps {}

export const Sidebar: React.FC<SidebarProps> = () => {
  const {pathname} = useLocation();
  const dispatch = useAppDispatch();
  const {user} = useAppSelector("auth");
  const {menuExpanded} = useAppSelector("layout");
  const CloseIcon = menuExpanded ? ChevronLeft : ChevronRight;
  const {black05, white05, hover, isDark, primary, black, blackWhite} =
    useTheme();
  const {name} = user;
  const defaultAvatar = buildAvatarPlaceholder(name.split(" ")[0]);

  useLayoutEffect(() => {
    const expanded = storage["read"]("[menu-expanded]");
    if (typeof expanded !== "boolean") dispatch(toggleMenu(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container style={{paddingTop: menuExpanded ? 8 : 42}}>
      <Absolute
        top={12}
        zIndex={7}
        right={menuExpanded ? 8 : "50%"}
        transform={`translateX(${menuExpanded ? 0 : "50%"})`}
      >
        <IconButton
          size={35}
          backgroundColor={menuExpanded ? hover : primary}
          onClick={() => dispatch(toggleMenu(!menuExpanded))}
          icon={
            <CloseIcon
              size={25}
              strokeWidth={2}
              color={menuExpanded ? blackWhite : black}
            />
          }
        />
      </Absolute>
      <Column gap={0}>
        <Row
          gap={10}
          marginTop="1rem"
          justifyContent="center"
          width={menuExpanded ? "auto" : "100%"}
        >
          <Frame
            children={<Avatar size={40} borderRadius={8} src={defaultAvatar} />}
          />
          {menuExpanded && (
            <Typography
              fontSize={18}
              className="line-clamp-1"
              fontFamily="Rubik-Medium"
            >
              {name}
            </Typography>
          )}
        </Row>
        <Line margin="1rem 0" />
        {sidebar.map(({module, routes}, key) => {
          return (
            <Column key={key} marginBottom={25}>
              <AnimatePresence>
                {module && menuExpanded && (
                  <motion.div
                    initial={{opacity: 0}}
                    style={{padding: "0 0.5rem"}}
                    animate={{opacity: 1, transition: {delay: 0.2}}}
                  >
                    <Row>
                      <Typography
                        fontSize={12}
                        letterSpacing={1.2}
                        textTransform="uppercase"
                        fontFamily="Rubik-SemiBold"
                        color={isDark ? white05 : black05}
                      >
                        {module["label"]}
                      </Typography>
                    </Row>
                  </motion.div>
                )}
              </AnimatePresence>
              <Column gap={2}>
                {routes.map(({label, icon: Icon, path}, key) => {
                  const highlighted = pathname.includes(path);

                  return (
                    <NavItem
                      to={path}
                      key={key}
                      style={{
                        width: menuExpanded ? "100%" : "auto",
                        backgroundColor: highlighted ? primary : undefined,
                      }}
                    >
                      <Row gap={10}>
                        <Icon
                          size={20}
                          strokeWidth={2}
                          style={{
                            fontSize: 20,
                            strokeWidth: 2,
                            color: highlighted ? black : blackWhite,
                          }}
                        />
                        <AnimatePresence>
                          {menuExpanded && (
                            <motion.div
                              style={{padding: "0 0.5rem"}}
                              initial={{
                                opacity: 0,
                                transform: "translateX(15px)",
                              }}
                              animate={{
                                opacity: 1,
                                transition: {delay: 0.1},
                                transform: "translateX(0px)",
                              }}
                            >
                              <Typography
                                fontFamily="Rubik-Medium"
                                color={highlighted ? black : blackWhite}
                              >
                                {label}
                              </Typography>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Row>
                    </NavItem>
                  );
                })}
              </Column>
            </Column>
          );
        })}
      </Column>
    </Container>
  );
};
